import path from 'path';
import {
  useContext,
  useCallback,
  useMemo,
} from 'react';

import * as Sentry from '@sentry/browser';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import FirebaseContext from '../context/FirebaseContext';
import { DateFormat } from '../utils/date';
import { prepareUploadBlob } from '../../utils/dataProcessing';

const StorageProcessState = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  INITIAL: 'INITIAL',
};

const useStorage = () => {
  const {
    firebase: {
      storage,
    },
  } = useContext(FirebaseContext);

  /**
   * Listens and handles state changes for a Firebase upload task.
   * @param {firebase.storage.UploadTask} uploadTask Firebase storage upload task
   * @param {String} dataUrl Url representation of the file to upload
   * @param {String} storageRef Firebase storage reference of the file to upload
   * @param {String} errorMessage Sentry extra error message
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if successful,
   * the file's storage reference
   */
  const attachListenerToStorageTask = useCallback(({
    uploadTask,
    dataUrl,
    storageRef,
    errorMessage,
    onProgress,
    fileId,
  }) => new Promise((resolve) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress({
          fileId,
          progress,
        });
      },
      (error) => {
        Sentry.captureException(error, {
          extra: {
            message: errorMessage,
            fileRef: storageRef,
          },
        });
        URL.revokeObjectURL(dataUrl);
        resolve({
          state: StorageProcessState.FAILED,
          fileId,
        });
      },
      async () => {
        const publicUrl = await uploadTask.snapshot.ref.getDownloadURL();
        const metadata = await uploadTask.snapshot.ref.getMetadata();
        URL.revokeObjectURL(dataUrl);
        resolve({
          state: StorageProcessState.SUCCESS,
          fileRef: storageRef,
          publicUrl,
          metadata,
          fileId,
        });
      },
    );
  }), []);

  /**
   * Uploads a file to Firebase storage
   * @param {String} dataUrl Url representation of the file to upload
   * @param {String} fileFormat Format of the file to upload
   * @param {String} storageRef Firebase storage reference of the file to upload
   * @param {String} fileName optional filename
   * @param {String} fileType optional file mime type
   * @return {Promise<Object>} Promise that resolves to an object containing the storage task state and, if successful,
   * the file's storage reference
   */
  const uploadAttachment = useCallback(async (
    dataUrl,
    fileFormat,
    storagePath,
    fileName = '',
    fileType = '',
    onProgress = () => {},
    fileId = '',
  ) => {
    const rootRef = storage.ref();
    // Prepared first: a HEIC upload is stored as JPEG, so the extension and the
    // content type both come from the bytes rather than the original file name.
    const {
      blob: fileDataBlob,
      fileFormat: storedFormat,
      fileType: storedType,
    } = await prepareUploadBlob({ url: dataUrl, fileFormat, fileType });

    const storageFileName = fileName
      ? `${fileName}-${uuidv4()}.${storedFormat}`
      : `${moment().format(DateFormat.DEFAULT_DATE_WITH_TIME_FORMAT)}-${uuidv4()}.${storedFormat}`;
    const storageRef = `${storagePath}/${storageFileName}`;

    const uploadTask = rootRef.child(storageRef).put(fileDataBlob, { contentType: storedType || storedFormat });

    return attachListenerToStorageTask({
      uploadTask,
      dataUrl,
      storageRef,
      onProgress,
      fileId,
    });
  }, [
    storage,
    attachListenerToStorageTask,
  ]);

  /**
   * Uploads multiple files to Firebase storage
   * @param {Object[]} filesToAdd Array of objects containing files info
   * @param {String} storagePath Firebase storage path were the files will be uploaded
   * @return {Promise<Object[]>} Promise that resolves into an array of objects with the upload results for all files
   */
  const uploadAttachments = useCallback((
    filesToAdd,
    storagePath,
    onProgress,
  ) => Promise.all(filesToAdd.map((fileDetail) => {
    const {
      dataUrl,
      format: fileFormat,
      fileName,
      fileType,
      fileId,
    } = fileDetail;

    return uploadAttachment(
      dataUrl,
      fileFormat,
      storagePath,
      fileName,
      fileType,
      onProgress,
      fileId,
    );
  })), [
    uploadAttachment,
  ]);

  /**
   * Deletes a file from Firebase storage
   * @param {String} storagePath Firebase storage path of the file to delete
   * @return {{state: String, fileRef: String}} Object that contains the result of the operation and it's file path
   */
  const deleteAttachment = useCallback(async (storagePath) => {
    try {
      await storage
        .ref()
        .child(storagePath)
        .delete();
      return {
        state: StorageProcessState.SUCCESS,
        fileRef: storagePath,
      };
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error occurred while deleting a file from storage',
          fileRef: storagePath,
        },
      });
      return {
        state: StorageProcessState.FAILED,
        fileRef: storagePath,
      };
    }
  }, [
    storage,
  ]);

  /**
   * Deletes multiple files from Firebase storage
   * @param {Object[]} filesToDelete Array of objects containing the Firebase storage path of the
   * files to delete
   * @return {Promise<Object[]>} Promise that resolves into an array of objects with the delete results for all files
   */
  const deleteAttachments = useCallback((filesToDelete) => Promise.all(filesToDelete.map((fileDetail) => {
    const { storagePath } = fileDetail;

    return deleteAttachment(storagePath);
  })), [
    deleteAttachment,
  ]);

  /**
   * Downloads a file from Firebase storage and returns the file object
   * @param {String} storagePath Firebase storage path of the file to download
   * @return {Promise<File>} Promise that resolves into a file object
   */
  const getBlobFileFromStoragePath = useCallback(async (storagePath) => {
    const storagePathToBlobFile = async () => {
      const downloadUrl = await storage
        .ref()
        .child(storagePath)
        .getDownloadURL();
      const fileData = await fetch(downloadUrl);
      const fileBlob = await fileData.blob();
      return new File([fileBlob], path.basename(storagePath), { type: fileBlob.type });
    };

    return storagePathToBlobFile();
  }, [
    storage,
  ]);

  /**
   * Downloads multiple files from Firebase storage and returns the file object for every file
   * @param {String[]} storagePaths Array of strings containing the Firebase storage paths
   * @return {Promise<File[]>} Promise that resolves into an array of file objects
   */
  const getBlobFilesFromStoragePathsArray = useCallback((storagePaths) => {
    const promises = storagePaths.map((storagePath) => getBlobFileFromStoragePath(storagePath));
    return Promise.all(promises);
  }, [
    getBlobFileFromStoragePath,
  ]);

  /**
   * Get the download URL of a file from Firebase storage
   * @param {String} storagePath Firebase storage path of the file to download
   * @return {Promise<String>} Promise that resolves into a string containing the download URL of the file
   */
  const getDownloadUrlFromStoragePath = useCallback(async (storagePath) => {
    const storagePathToBlobURL = async () => {
      const downloadUrl = await storage
        .ref()
        .child(storagePath)
        .getDownloadURL();
      return downloadUrl;
    };

    return storagePathToBlobURL();
  }, [
    storage,
  ]);

  /**
   * Get the download URLs of multiple files from Firebase storage
   * @param {String[]} storagePaths Array of strings containing the Firebase storage paths
   * @return {Promise<String[]>} Promise that resolves into an array of strings containing the
   * download URLs of the files
   */
  const getDownloadUrlsFromStoragePathsArray = useCallback((storagePaths) => {
    const promises = storagePaths.map(async (storagePath) => ({
      storagePath,
      dataUrl: await getDownloadUrlFromStoragePath(storagePath),
    }));
    return Promise.all(promises);
  }, [
    getDownloadUrlFromStoragePath,
  ]);

  return useMemo(() => ({
    getBlobFileFromStoragePath,
    getBlobFilesFromStoragePathsArray,
    getDownloadUrlFromStoragePath,
    getDownloadUrlsFromStoragePathsArray,
    uploadAttachment,
    uploadAttachments,
    deleteAttachment,
    deleteAttachments,
  }), [
    getBlobFileFromStoragePath,
    getBlobFilesFromStoragePathsArray,
    getDownloadUrlFromStoragePath,
    getDownloadUrlsFromStoragePathsArray,
    uploadAttachment,
    uploadAttachments,
    deleteAttachment,
    deleteAttachments,
  ]);
};

export default useStorage;
export {
  StorageProcessState,
};
